import type { ExtractionCitation } from '@ctw/shared/api/ai/use-extraction-jobs';
import { getBinaryDocument } from '@ctw/shared/api/fhir/binaries';
import {
  type ExportDocumentInfo,
  type ExportField,
  isDeliverable,
} from '@ctw/shared/content/fact-extraction/compute-export-summary';
import {
  assignFileNames,
  buildExportZip,
  buildManifest,
  type ExportDocumentResult,
  sanitizeFileName,
} from '@ctw/shared/content/fact-extraction/export-package';
import { generateAtlasCsv } from '@ctw/shared/content/fact-extraction/generate-atlas-csv';
import type { CTWState } from '@ctw/shared/context/ctw-context';
import { type Env, getBffApiBaseUrl } from '@ctw/shared/utils/environments';
import { format } from 'date-fns';

const CONCURRENCY = 4;
// Attempts per document (1 initial + retries). A single flaky document must
// never abort the export — after the last attempt it becomes a 'failed'
// manifest entry instead.
const MAX_ATTEMPTS = 2;

export interface AtlasExportProgress {
  completedDocuments: number;
  totalDocuments: number;
}

export interface AtlasExportDeps {
  env: Env;
  // FHIR Patient id (not UPID) — what /v1/cda/pdf validates against.
  patientId: string;
  // Getters, not captured values: a worst-case export runs ~40-90s and must
  // pick up refreshed auth tokens (ctwFetch does not refresh Authorization).
  getAuthToken: () => string;
  getFetchFromFqs: () => CTWState['fetchFromFqs'];
  // Injectable for tests; defaults to the global fetch.
  fetchFn?: typeof fetch;
}

export interface RunAtlasExportArgs {
  deps: AtlasExportDeps;
  // The CSV is generated here, AFTER document fetching, so its
  // sourceDocuments column only references files actually in the zip.
  fields: Array<ExportField>;
  citations: Array<ExtractionCitation>;
  patientName: string;
  upid: string;
  jobId: string;
  configName?: string;
  // Deduped source documents cited by included fields (deliverable or not) —
  // typically ExportSummary.includedSources.
  exportDocuments: Array<ExportDocumentInfo>;
  includedFieldsCount: number;
  excludedFieldsCount: number;
  documentedIncludedCount: number;
  signal: AbortSignal;
  onProgress: (progress: AtlasExportProgress) => void;
}

export interface AtlasExportResult {
  blob: Blob;
  fileName: string;
  includedCount: number;
  failedCount: number;
  unsupportedCount: number;
}

const abortError = () => new DOMException('Atlas export cancelled', 'AbortError');

const isAbortError = (error: unknown): boolean =>
  error instanceof DOMException && error.name === 'AbortError';

async function mapWithConcurrency<T, R>(
  items: Array<T>,
  limit: number,
  fn: (item: T) => Promise<R>,
): Promise<Array<R>> {
  const results = new Array<R>(items.length);
  let nextIndex = 0;
  const workers = Array.from({ length: Math.min(limit, items.length) }, async () => {
    while (nextIndex < items.length) {
      const index = nextIndex;
      nextIndex += 1;
      results[index] = await fn(items[index]);
    }
  });
  await Promise.all(workers);
  return results;
}

async function fetchDocumentPdf(
  doc: ExportDocumentInfo,
  { deps, signal }: Pick<RunAtlasExportArgs, 'deps' | 'signal'>,
): Promise<Uint8Array> {
  if (doc.status === 'pdf') {
    // Already a PDF: fetch the binary straight from FQS, no rendering.
    // fetchFromFqs doesn't take an AbortSignal; cancellation is checked
    // between documents instead.
    const binaryData = await getBinaryDocument(deps.getFetchFromFqs(), doc.binaryId);
    return new Uint8Array(await binaryData.binary.blob.arrayBuffer());
  }
  // CCDA: render server-side via the existing bff-api endpoint, exactly as
  // the document viewer's download button does (full document, no sectionIds).
  const fetchFn = deps.fetchFn ?? fetch;
  const response = await fetchFn(`${getBffApiBaseUrl(deps.env)}/v1/cda/pdf`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${deps.getAuthToken()}`,
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({
      patientId: deps.patientId,
      documentReferenceId: doc.documentReferenceId,
    }),
    signal,
  });
  if (!response.ok) {
    throw new Error(`CDA PDF render failed with status ${response.status}`);
  }
  return new Uint8Array(await (await response.blob()).arrayBuffer());
}

// Retries transient failures; aborts escape immediately and are never retried.
async function fetchDocumentPdfWithRetry(
  doc: ExportDocumentInfo,
  args: Pick<RunAtlasExportArgs, 'deps' | 'signal'>,
): Promise<Uint8Array> {
  let lastError: unknown;
  for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt += 1) {
    try {
      return await fetchDocumentPdf(doc, args);
    } catch (error) {
      if (isAbortError(error) || args.signal.aborted) {
        throw abortError();
      }
      lastError = error;
    }
  }
  throw lastError;
}

export async function runAtlasExport(args: RunAtlasExportArgs): Promise<AtlasExportResult> {
  const { exportDocuments, signal, onProgress } = args;
  let completedDocuments = 0;

  const results = await mapWithConcurrency(
    exportDocuments,
    CONCURRENCY,
    async (doc): Promise<ExportDocumentResult> => {
      if (signal.aborted) {
        throw abortError();
      }
      try {
        if (!isDeliverable(doc)) {
          return { document: doc, outcome: 'unsupported', reason: doc.reason };
        }
        try {
          return {
            document: doc,
            outcome: 'included',
            bytes: await fetchDocumentPdfWithRetry(doc, args),
          };
        } catch (error) {
          if (isAbortError(error)) {
            throw error; // cancellation propagates; a failed document never does
          }
          return {
            document: doc,
            outcome: 'failed',
            reason: 'Could not retrieve or render this document',
          };
        }
      } finally {
        completedDocuments += 1;
        onProgress({ completedDocuments, totalDocuments: exportDocuments.length });
      }
    },
  );

  if (signal.aborted) {
    throw abortError();
  }

  assignFileNames(results);

  // Citation ref → zip file name, from documents that actually made it in.
  // This is what lets a CSV row point at the exact file backing its citation.
  const fileNameByCitationRef = new Map<string, string>();
  for (const result of results) {
    if (result.outcome === 'included' && result.fileName) {
      for (const ref of result.document.citationRefs ?? []) {
        if (!fileNameByCitationRef.has(ref)) {
          fileNameByCitationRef.set(ref, result.fileName);
        }
      }
    }
  }
  const csv = generateAtlasCsv({
    fields: args.fields,
    citations: args.citations,
    fileNameByCitationRef,
  });

  const generatedAt = new Date();
  const manifest = buildManifest({
    patientName: args.patientName,
    patientId: args.deps.patientId,
    upid: args.upid,
    jobId: args.jobId,
    configName: args.configName,
    generatedAt,
    includedFieldsCount: args.includedFieldsCount,
    excludedFieldsCount: args.excludedFieldsCount,
    documentedIncludedCount: args.documentedIncludedCount,
    results,
  });
  const blob = await buildExportZip({ csv, manifest, results });

  return {
    blob,
    fileName: `${sanitizeFileName(`${args.patientName} Atlas Export ${format(generatedAt, 'dd-MM-yyyy')}`)}.zip`,
    includedCount: results.filter((result) => result.outcome === 'included').length,
    failedCount: results.filter((result) => result.outcome === 'failed').length,
    unsupportedCount: results.filter((result) => result.outcome === 'unsupported').length,
  };
}
